import BaseController from "./base_controller"

// Connects to data-controller="confirmation"
//
// Generic "are you sure?" dialog for links that navigate or submit. The
// delete-specific flow (toasts, page refresh) lives in
// delete_confirmation_controller.
export default class extends BaseController {
  static targets = ["modal", "confirmButton", "cancelButton", "title", "message"]
  static values = {
    url: String,
    method: { type: String, default: 'GET' },
    turbo: { type: Boolean, default: true }
  }

  declare readonly modalTarget: HTMLDialogElement
  declare readonly confirmButtonTarget: HTMLButtonElement
  declare readonly titleTarget: HTMLElement
  declare readonly messageTarget: HTMLElement
  declare readonly hasModalTarget: boolean
  declare readonly hasTitleTarget: boolean
  declare readonly hasMessageTarget: boolean

  declare urlValue: string
  declare methodValue: string
  declare turboValue: boolean

  // Show modal with confirmation
  show(event: MouseEvent): void {
    event.preventDefault()

    if (!this.hasModalTarget) {
      this.handleError(new Error('No modalTarget available'), 'show')
      return
    }

    const button = event.currentTarget as HTMLElement
    this.urlValue = button.dataset.confirmationUrlValue || ''
    this.methodValue = button.dataset.confirmationMethodValue || 'GET'

    const title = button.dataset.confirmationTitleValue
    const message = button.dataset.confirmationMessageValue

    if (this.hasTitleTarget && title) {
      this.titleTarget.textContent = title
    }

    if (this.hasMessageTarget && message) {
      this.messageTarget.textContent = message
    }

    this.modalTarget.showModal()
  }

  // Handle confirmation
  confirm(event: MouseEvent): void {
    event.preventDefault()

    if (!this.urlValue) {
      this.handleError(new Error('No url value provided'), 'confirm')
      return
    }

    const method = this.methodValue.toUpperCase()

    if (method === 'GET') {
      if (this.turboValue && window.Turbo) {
        window.Turbo.visit(this.urlValue)
      } else {
        window.location.href = this.urlValue
      }
    } else {
      // createTurboForm supplies the CSRF token and the _method override, and
      // submitTurboForm removes the form once Turbo reports the submit ended.
      this.submitTurboForm(this.createTurboForm(this.urlValue, method))
    }

    this.close()
  }

  // Handle cancellation
  cancel(event: MouseEvent): void {
    event.preventDefault()
    this.close()
  }

  close(): void {
    if (this.hasModalTarget) {
      this.modalTarget.close()
    }
  }

  // Close on backdrop click
  clickOutside(event: MouseEvent): void {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }
}
